import {
  Cart,
  CartTotalResponse,
  CartTotalResponseAppliedVoucher,
  CartTotalResponseBundledDiscount,
  CartTotalResponseBundledFreeGoods,
  CartTotalResponseCompiledProductDiscount,
  CartTotalResponsePromo,
  CartTotalResponseSingleDiscount,
  CartTotalResponseSingleFreeGood,
} from '@diamond/shared/types';

function generateUUID(): string {
  return crypto.randomUUID();
}

function generateDateRange(): { valid_from: number; valid_until: number } {
  const now = new Date();
  const valid_from = new Date(now);
  valid_from.setMonth(now.getMonth() - 1);
  const valid_until = new Date(now);
  valid_until.setMonth(now.getMonth() + 2);

  return {
    valid_from: valid_from.getTime(),
    valid_until: valid_until.getTime(),
  };
}

function createFakeProduct(
  overrides?: Partial<Cart['product']>
): Cart['product'] {
  const productId = generateUUID();
  const itemCode = Math.floor(Math.random() * 10000000) + 10000000; // More realistic item codes
  const baseProduct: Cart['product'] = {
    id: productId,
    title: 'FRESH MILK PLAIN FS 946 ML',
    desc: 'DIAMOND FRESH MILK FS FOR PROFESSIONAL 946 ML',
    type: 'ZFGD',
    pack_unit: '12X946 ML',
    information:
      'Susu Segar Diamond memiliki rasa alami yang segar dan kandungan nutrisi yang sangat baik untuk kesehatan Anda. Selain nilai nutrisinya, Diamond Fresh Milk juga enak diminum, baik langsung maupun dicampur dengan kopi, milk shake dan banyak lagi.',
    composition: '',
    superiority:
      '100% Susu Segar Berkualitas\r\nTanpa Bahan Pengawet\r\nRasa yang lezat & segar\r\nDi kemas secara aseptik\r\nDidatangkan dari koperasi susu terbaik setiap hari',
    item_code: itemCode,
    brand_name: 'DIAMOND',
    brand_desc: 'DIAMOND',
    division: 'diamond',
    product_storage: 'chilled',
    barcode: ' ',
    measurement_unit: 'PC',
    measurement_unit_desc: 'Piece',
    sales_unit: 'CAR',
    sales_unit_desc: 'Carton',
    dimension: '29,800 X 22,500 X 23,900',
    dimension_unit: 'CM',
    gross_weight: 1.011,
    net_weight: 0.972,
    weight_unit: 'KG',
    gross_weight_desc: '1.011 KG',
    net_weight_desc: '0.972 KG',
    volume: 1151.5,
    volume_unit: 'CCM',
    minimum_order_quantity: 1000,
    order_quantity_multiple: 1000,
    stock: 19036000,
    stock_status: 'AVAILABLE',
    category_name: 'DAIRY',
    wishlist_id: null,
    sub_category_name: 'DAIRY MILK',
    sub_third_category_name: 'ESL',
    product_images: [
      `https://diamond-file-uploads-staging.s3.ap-southeast-1.amazonaws.com/product-images/${itemCode}_1.jpg`,
    ],
    product_image_thumbnails: [
      `https://diamond-file-uploads-staging.s3.ap-southeast-1.amazonaws.com/product-images/thumbnails/${itemCode}_1_thumbnail_150.webp`,
    ],
    product_images_medium: [
      `https://diamond-file-uploads-staging.s3.ap-southeast-1.amazonaws.com/product-images/medium/${itemCode}_1_medium_400.webp`,
    ],
    product_images_large: [
      `https://diamond-file-uploads-staging.s3.ap-southeast-1.amazonaws.com/product-images/large/${itemCode}_1_large_700.webp`,
    ],
    prices: [{ price: 210000, min_order_quantity: 1 }],
    stock_magnifier: 1000,
    free_good_promos: [],
    discount_promos: [],
    bundled_discount_promos: [],
    bundled_free_good_promos: [],
  };

  return { ...baseProduct, ...overrides };
}

export function createFakeCartItems(
  options: {
    count?: number;
    divisions?: Array<'diamond' | 'trading' | 'fnb' | 'equipment'>;
    includePromos?: boolean;
  } = {}
): Cart[] {
  const {
    count = 5,
    divisions = ['diamond', 'trading', 'fnb'],
    includePromos = false,
  } = options;

  const cartItems: Cart[] = [];
  const userId = generateUUID();
  const warehouseId = generateUUID();

  for (let i = 0; i < count; i++) {
    const division = divisions[i % divisions.length];
    // Create varied products based on division
    const productTitles = {
      diamond: [
        'FRESH MILK PLAIN FS 946 ML',
        'CHEESE SLICES 200G',
        'YOGURT GREEK 500G',
        'BUTTER UNSALTED 250G',
      ],
      trading: [
        'RICE PREMIUM 5KG',
        'SUGAR REFINED 1KG',
        'COFFEE BEANS 500G',
        'TEA BLACK 100BAGS',
      ],
      fnb: [
        'CHICKEN BREAST 1KG',
        'BEEF GROUND 500G',
        'SALMON FILLET 300G',
        'EGGS ORGANIC DOZEN',
      ],
      equipment: [
        'MIXER PROFESSIONAL 5L',
        'OVEN CONVECTION 20L',
        'REFRIGERATOR COMMERCIAL',
        'COFFEE MACHINE ESPRESSO',
      ],
    };

    const titles =
      productTitles[division as keyof typeof productTitles] ||
      productTitles.diamond;
    const title = titles[i % titles.length];

    const product = createFakeProduct({
      division,
      id: generateUUID(),
      title,
      item_code: 10000000 + i * 1000 + Math.floor(Math.random() * 1000),
      stock: i % 2 === 0 ? 19036000 - i * 1000000 : 0,
      stock_status: i % 2 === 0 ? 'AVAILABLE' : 'OUT_OF_STOCK',
      minimum_order_quantity: 1000,
      order_quantity_multiple: 1000,
      prices: [{ price: 210000 + i * 50000, min_order_quantity: 1 }],
    });

    const cartItem: Cart = {
      id: generateUUID(),
      user_id: userId,
      warehouse_id: warehouseId,
      quantity: 5000 + i * 1000, // More realistic quantities based on real data
      note: i % 3 === 0 ? `Note for item ${i + 1}` : null,
      product,
    };

    cartItems.push(cartItem);
  }

  return cartItems;
}

export function createFakeSingleDiscountPromo(
  overrides?: Partial<CartTotalResponseSingleDiscount>
): CartTotalResponseSingleDiscount {
  const { valid_from, valid_until } = generateDateRange();
  const productId = generateUUID();
  const basePrice = 210000;
  const defaultPercent = 20;
  const isDiscountPercentage = overrides?.is_discount ?? true;
  const calculationNumber = overrides?.calculation_number ?? defaultPercent;
  const defaultDeductionAmount = isDiscountPercentage
    ? calculationNumber * 100
    : 50000;
  const deductionAmount = overrides?.deduction_amount ?? defaultDeductionAmount;
  const defaultPriceAfter = isDiscountPercentage
    ? basePrice - Math.floor((basePrice * calculationNumber) / 100)
    : Math.max(basePrice - deductionAmount, 0);
  const priceAfterDiscount =
    overrides?.price_after_discount ?? defaultPriceAfter;

  return {
    id: generateUUID(),
    mechanism: 'Beli 3 DISKON 20%',
    promo_code: `PROMO${Math.floor(Math.random() * 10000)}`,
    valid_from,
    valid_until,
    is_discount: isDiscountPercentage,
    deduction_amount: deductionAmount,
    calculation_number: calculationNumber,
    minimum_order_quantity: 3000,
    minimum_order_amount: 150000,
    is_applies_in_multiples: true,
    price_after_discount: priceAfterDiscount,
    product_id: productId,
    ...overrides,
  };
}

export function createFakeSingleFreeGoodPromo(
  overrides?: Partial<CartTotalResponseSingleFreeGood>
): CartTotalResponseSingleFreeGood {
  const { valid_from, valid_until } = generateDateRange();
  const productId = generateUUID();
  const refProductId = generateUUID();

  const basePromo = {
    id: generateUUID(),
    mechanism: 'Beli 3 Gratis 1',
    promo_code: `PROMO${Math.floor(Math.random() * 10000)}`,
    valid_from,
    valid_until,
    sequential_number_for_free_goods_scale: 1,
    free_goods_category: 'inclusive' as const,
    quantity_free_goods: 1000,
    minimum_order_quantity: 3000,
    minimum_order_amount: 150000,
    ref_product_id: refProductId,
    is_applies_in_multiples: true,
    product_id: productId,
    product_item_code: 100002,
    product_images: [
      'https://dummyimage.com/700x700/007aff/ffffff&text=Free+Good',
    ],
    product_image_thumbnails: [
      'https://dummyimage.com/150x150/007aff/ffffff&text=Free+Good',
    ],
    product_images_medium: [
      'https://dummyimage.com/400x400/007aff/ffffff&text=Free+Good',
    ],
    product_images_large: [
      'https://dummyimage.com/700x700/007aff/ffffff&text=Free+Good',
    ],
    title: 'Free Product',
    sales_unit: 'BOX',
    sales_unit_desc: 'Box',
    price: 210000,
    ...overrides,
  };

  return basePromo;
}

export function createFakeBundledDiscountPromo(
  overrides?: Partial<CartTotalResponseBundledDiscount>
): CartTotalResponseBundledDiscount {
  const { valid_from, valid_until } = generateDateRange();

  return {
    id: generateUUID(),
    mechanism: 'Beli Paket DISKON 10%',
    promo_code: `PROMO${Math.floor(Math.random() * 10000)}`,
    valid_from,
    valid_until,
    is_discount: true,
    deduction_amount: 1000,
    calculation_number: 10,
    sum_minimum_order_quantity: 5000,
    sum_minimum_order_amount: 250000,
    ...overrides,
  };
}

export function createFakeBundledFreeGoodPromo(
  overrides?: Partial<CartTotalResponseBundledFreeGoods>
): CartTotalResponseBundledFreeGoods {
  const { valid_from, valid_until } = generateDateRange();
  const productId = generateUUID();

  const basePromo = {
    id: generateUUID(),
    mechanism: 'Beli Paket Gratis 2',
    promo_code: `PROMO${Math.floor(Math.random() * 10000)}`,
    valid_from,
    valid_until,
    sequential_number_for_free_goods_scale: 1,
    free_goods_category: 'inclusive' as const,
    quantity_free_goods: 2000,
    sum_minimum_order_quantity: 5000,
    sum_minimum_order_amount: 250000,
    product_id: productId,
    product_item_code: 100004,
    product_images: [
      'https://dummyimage.com/700x700/34c759/ffffff&text=Free+Bundle',
    ],
    product_image_thumbnails: [
      'https://dummyimage.com/150x150/34c759/ffffff&text=Free+Bundle',
    ],
    product_images_medium: [
      'https://dummyimage.com/400x400/34c759/ffffff&text=Free+Bundle',
    ],
    product_images_large: [
      'https://dummyimage.com/700x700/34c759/ffffff&text=Free+Bundle',
    ],
    title: 'Bundled Free Product',
    sales_unit: 'BOX',
    sales_unit_desc: 'Box',
    price: 210000,
    ...overrides,
  };

  return basePromo;
}

export function createFakeCompiledProductDiscount(
  productId: string,
  overrides?: Partial<CartTotalResponseCompiledProductDiscount>
): CartTotalResponseCompiledProductDiscount {
  return {
    product_id: productId,
    is_discount: true,
    deduction_amount: 20000,
    price_after_discount: 80000,
    ...overrides,
  };
}

export function createFakeVoucherDiscountPercent(
  overrides?: Partial<CartTotalResponseAppliedVoucher>
): CartTotalResponseAppliedVoucher {
  const { valid_from, valid_until } = generateDateRange();
  const productId = generateUUID();
  const originalPrice = 210000;
  const discountPercent = 20;
  const discountAmount = Math.floor(originalPrice * (discountPercent / 100));

  return {
    voucher_id: generateUUID(),
    voucher_code: `VOUCHER${Math.floor(Math.random() * 10000)}`,
    voucher_title: 'Diskon 20%',
    reward_type: 'DISCOUNT_PERCENT',
    valid_from: new Date(valid_from).toISOString(),
    valid_until: new Date(valid_until).toISOString(),
    rule_products: [
      {
        product_id: productId,
        product_code: 'PROD001',
        product_item_code: 100001,
        min_quantity: 1000,
      },
    ],
    product_discounts: [
      {
        product_id: productId,
        product_code: 'PROD001',
        product_item_code: 100001,
        original_price: originalPrice,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        final_price: originalPrice - discountAmount,
        product_images: [],
        product_images_medium: [],
        product_images_large: [],
        product_image_thumbnails: [],
      },
    ],
    free_goods: undefined,
    ...overrides,
  };
}

export function createFakeVoucherDiscountAmount(
  overrides?: Partial<CartTotalResponseAppliedVoucher>
): CartTotalResponseAppliedVoucher {
  const { valid_from, valid_until } = generateDateRange();
  const productId = generateUUID();
  const originalPrice = 420000;
  const discountAmount = 100000;
  const derivedPercent = Math.round((discountAmount / originalPrice) * 100);

  return {
    voucher_id: generateUUID(),
    voucher_code: `VOUCHER${Math.floor(Math.random() * 10000)}`,
    voucher_title: 'Diskon Rp 50.000',
    reward_type: 'DISCOUNT_AMOUNT',
    valid_from: new Date(valid_from).toISOString(),
    valid_until: new Date(valid_until).toISOString(),
    rule_products: [
      {
        product_id: productId,
        product_code: 'PROD002',
        product_item_code: 100002,
        min_quantity: 2000,
      },
    ],
    product_discounts: [
      {
        product_id: productId,
        product_code: 'PROD002',
        product_item_code: 100002,
        original_price: originalPrice,
        discount_percent: derivedPercent,
        discount_amount: discountAmount,
        final_price: originalPrice - discountAmount,
        product_images: [],
        product_images_medium: [],
        product_images_large: [],
        product_image_thumbnails: [],
      },
    ],
    free_goods: undefined,
    ...overrides,
  };
}

export function createFakeVoucherFreeGoods(
  overrides?: Partial<CartTotalResponseAppliedVoucher>
): CartTotalResponseAppliedVoucher {
  const { valid_from, valid_until } = generateDateRange();
  const productId = generateUUID();
  const freeGoodProductId = generateUUID();

  return {
    voucher_id: generateUUID(),
    voucher_code: `VOUCHER${Math.floor(Math.random() * 10000)}`,
    voucher_title: 'Gratis Produk',
    reward_type: 'FREE_GOODS',
    valid_from: new Date(valid_from).toISOString(),
    valid_until: new Date(valid_until).toISOString(),
    rule_products: [
      {
        product_id: productId,
        product_code: 'PROD003',
        product_item_code: 100003,
        min_quantity: 3000,
      },
    ],
    product_discounts: undefined,
    free_goods: [
      {
        product_id: freeGoodProductId,
        product_code: 'FREE001',
        product_item_code: 200001,
        product_name: 'Free Product',
        product_brand_name: 'Free Brand',
        quantity: 1000,
        division: 'trading',
        product_images: [
          'https://dummyimage.com/700x700/ff9500/ffffff&text=Voucher+Free+Good',
        ],
        product_images_medium: [
          'https://dummyimage.com/400x400/ff9500/ffffff&text=Voucher+Free+Good',
        ],
        product_images_large: [
          'https://dummyimage.com/700x700/ff9500/ffffff&text=Voucher+Free+Good',
        ],
        product_image_thumbnails: [
          'https://dummyimage.com/150x150/ff9500/ffffff&text=Voucher+Free+Good',
        ],
        saved_amount: 50000,
      },
    ],
    ...overrides,
  };
}

export function createFakeCartTotalResponse(
  options: {
    cartItems?: Cart[];
    includePromos?: boolean;
    includeVouchers?: boolean;
    voucherTypes?: Array<'DISCOUNT_PERCENT' | 'DISCOUNT_AMOUNT' | 'FREE_GOODS'>;
    divisions?: Array<'diamond' | 'trading' | 'fnb' | 'equipment'>;
  } = {}
): CartTotalResponse {
  const {
    cartItems = createFakeCartItems(),
    includePromos = true,
    includeVouchers = true,
    voucherTypes = ['DISCOUNT_PERCENT', 'DISCOUNT_AMOUNT', 'FREE_GOODS'],
    divisions = ['diamond', 'trading', 'fnb'],
  } = options;

  let originalTotal = 0;
  let finalTotal = 0;
  const promos: CartTotalResponse['promos'] = [];
  const compiledProductDiscounts: CartTotalResponseCompiledProductDiscount[] =
    [];

  cartItems.forEach((item) => {
    const basePrice = item.product.prices[0]?.price || 50000;
    const quantity = item.quantity;
    const itemTotal = (basePrice * quantity) / 1000;
    originalTotal += itemTotal;

    compiledProductDiscounts.push(
      createFakeCompiledProductDiscount(item.product.id, {
        deduction_amount: Math.floor(itemTotal * 0.1),
        price_after_discount: Math.floor(itemTotal * 0.9),
      })
    );
  });

  finalTotal = originalTotal * 0.9;
  const savedTotal = originalTotal - finalTotal;

  if (includePromos) {
    divisions.forEach((division) => {
      const divisionPromos: CartTotalResponsePromo = {
        division,
        single_discount_promos: [],
        single_free_good_promos: [],
        bundled_discount_promos: [],
        bundled_free_good_promos: [],
        compiled_product_discounts: compiledProductDiscounts.filter(
          (discount) => {
            const cartItem = cartItems.find(
              (item) => item.product.id === discount.product_id
            );
            return cartItem?.product.division === division;
          }
        ),
      };

      const divisionCartItems = cartItems.filter(
        (item) =>
          item.product.division === division &&
          item.product.stock_status === 'AVAILABLE'
      );

      if (divisionCartItems.length > 0) {
        const firstProduct = divisionCartItems[0].product;
        divisionPromos.single_discount_promos.push(
          createFakeSingleDiscountPromo({
            product_id: firstProduct.id,
            mechanism: 'Beli 3 DISKON 20%',
            calculation_number: 20,
            deduction_amount: 2000,
            minimum_order_quantity: 3000,
            is_applies_in_multiples: true,
          })
        );

        if (divisionCartItems.length > 1) {
          const secondProduct = divisionCartItems[1].product;
          divisionPromos.single_free_good_promos.push(
            createFakeSingleFreeGoodPromo({
              product_id: secondProduct.id,
              ref_product_id: firstProduct.id,
              mechanism: 'Beli 3 Gratis 1',
              quantity_free_goods: 1000,
              minimum_order_quantity: 3000,
              is_applies_in_multiples: true,
            })
          );
        }

        if (divisionCartItems.length > 2) {
          const thirdProduct = divisionCartItems[2].product;
          divisionPromos.single_discount_promos.push(
            createFakeSingleDiscountPromo({
              product_id: thirdProduct.id,
              mechanism: 'Beli 5 DISKON Rp 50.000',
              calculation_number: 0,
              deduction_amount: 50000,
              is_discount: false,
              minimum_order_quantity: 5000,
              is_applies_in_multiples: false,
            })
          );
        }

        divisionPromos.bundled_discount_promos.push(
          createFakeBundledDiscountPromo({
            mechanism: 'Beli Paket DISKON 10%',
            calculation_number: 10,
            sum_minimum_order_quantity: 5000,
          })
        );
        divisionPromos.bundled_free_good_promos.push(
          createFakeBundledFreeGoodPromo({
            mechanism: 'Beli Paket Gratis 2',
            quantity_free_goods: 2000,
            sum_minimum_order_quantity: 5000,
          })
        );
      }

      promos.push(divisionPromos);
    });
  }

  let vouchers: CartTotalResponse['vouchers'] | undefined;
  if (includeVouchers && cartItems.length > 0) {
    const appliedVouchers: CartTotalResponseAppliedVoucher[] = [];
    const availableCartItems = cartItems.filter(
      (item) => item.product.stock_status === 'AVAILABLE'
    );

    if (
      voucherTypes.includes('DISCOUNT_PERCENT') &&
      availableCartItems.length > 0
    ) {
      const item = availableCartItems[0];
      appliedVouchers.push(
        createFakeVoucherDiscountPercent({
          rule_products: [
            {
              product_id: item.product.id,
              product_code: `PROD${item.product.item_code}`,
              product_item_code: item.product.item_code,
              min_quantity: 1000,
            },
          ],
          product_discounts: [
            {
              product_id: item.product.id,
              product_code: `PROD${item.product.item_code}`,
              product_item_code: item.product.item_code,
              original_price: item.product.prices[0]?.price || 100000,
              discount_percent: 20,
              discount_amount: Math.floor(
                (item.product.prices[0]?.price || 100000) * 0.2
              ),
              final_price: Math.floor(
                (item.product.prices[0]?.price || 100000) * 0.8
              ),
              product_images: [],
              product_images_medium: [],
              product_images_large: [],
              product_image_thumbnails: [],
            },
          ],
        })
      );
    }

    if (
      voucherTypes.includes('DISCOUNT_AMOUNT') &&
      availableCartItems.length > 1
    ) {
      const item = availableCartItems[1];
      appliedVouchers.push(
        createFakeVoucherDiscountAmount({
          rule_products: [
            {
              product_id: item.product.id,
              product_code: `PROD${item.product.item_code}`,
              product_item_code: item.product.item_code,
              min_quantity: 2000,
            },
          ],
          product_discounts: [
            {
              product_id: item.product.id,
              product_code: `PROD${item.product.item_code}`,
              product_item_code: item.product.item_code,
              original_price: item.product.prices[0]?.price || 200000,
              discount_percent: 0,
              discount_amount: 50000,
              final_price: (item.product.prices[0]?.price || 200000) - 50000,
              product_images: [],
              product_images_medium: [],
              product_images_large: [],
              product_image_thumbnails: [],
            },
          ],
        })
      );
    }

    if (voucherTypes.includes('FREE_GOODS') && availableCartItems.length > 2) {
      const item = availableCartItems[2];
      appliedVouchers.push(
        createFakeVoucherFreeGoods({
          rule_products: [
            {
              product_id: item.product.id,
              product_code: `PROD${item.product.item_code}`,
              product_item_code: item.product.item_code,
              min_quantity: 3000,
            },
          ],
          free_goods: [
            {
              product_id: generateUUID(),
              product_code: 'FREE001',
              product_item_code: 200001,
              product_name: 'Free Product',
              product_brand_name: 'Free Brand',
              quantity: 1,
              division: item.product.division,
              product_images: [
                'https://dummyimage.com/700x700/ff9500/ffffff&text=Voucher+Free+Good',
              ],
              product_images_medium: [
                'https://dummyimage.com/400x400/ff9500/ffffff&text=Voucher+Free+Good',
              ],
              product_images_large: [
                'https://dummyimage.com/700x700/ff9500/ffffff&text=Voucher+Free+Good',
              ],
              product_image_thumbnails: [
                'https://dummyimage.com/150x150/ff9500/ffffff&text=Voucher+Free+Good',
              ],
              saved_amount: 50000,
            },
          ],
        })
      );
    }

    const totalVoucherDiscount = appliedVouchers.reduce(
      (sum: number, voucher: CartTotalResponseAppliedVoucher) => {
        if (voucher.product_discounts) {
          return (
            sum +
            voucher.product_discounts.reduce(
              (discountSum: number, discount) =>
                discountSum + discount.discount_amount,
              0
            )
          );
        }
        return sum;
      },
      0
    );

    vouchers = {
      applied_vouchers: appliedVouchers,
      total_voucher_discount: totalVoucherDiscount,
      invalid_vouchers: [],
    };
  }

  return {
    original_total: Math.floor(originalTotal * 1000),
    final_total: Math.floor(finalTotal * 1000),
    saved_total: Math.floor(savedTotal * 1000),
    promos,
    vouchers,
  };
}
